import React from 'react';
import { Box, Container, Typography, Link, Divider } from '@mui/material';
import { useTenant } from '../../../contexts/TenantContext';

interface SaasFooterProps {
  // Optional prop to show additional links or content
  showLinks?: boolean;
  // Optional prop to override the tenant name display
  companyNameOverride?: string;
  // Optional prop for additional custom content
  additionalContent?: React.ReactNode;
}

const SaasFooter: React.FC<SaasFooterProps> = ({
  showLinks = true,
  companyNameOverride,
  additionalContent,
}) => {
  const { tenant, getConfigValue } = useTenant();

  // Get current year
  const currentYear = new Date().getFullYear();

  // Get company name - use override, then config, then capitalize tenant name
  const companyName =
    companyNameOverride ||
    getConfigValue<string>('COMPANY_NAME', '') ||
    tenant.charAt(0).toUpperCase() + tenant.slice(1);

  // Optional links that could be configured per tenant
  const privacyLink = getConfigValue<string>('PRIVACY_POLICY_URL', '');
  const termsLink = getConfigValue<string>('TERMS_OF_SERVICE_URL', '');
  const supportEmail = getConfigValue<string>('SUPPORT_EMAIL', '');

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.secondary',
        borderTop: '1px solid',
        borderTopColor: 'grey.200',
        py: 3,
        px: 2,
        mt: 'auto', // Push footer to bottom if using flex layout
        width: '100%',
      }}
    >
      <Container maxWidth="lg">
        {/* Additional content section if provided */}
        {additionalContent && (
          <>
            <Box sx={{ mb: 3 }}>{additionalContent}</Box>
            <Divider
              sx={{
                borderColor: 'grey.300',
                mb: 3,
              }}
            />
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'center' },
            gap: 2,
          }}
        >
          {/* Copyright Section */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            © {currentYear} {companyName}. All rights reserved.
          </Typography>

          {/* Optional Links Section */}
          {showLinks && (privacyLink || termsLink || supportEmail) && (
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'flex-end' },
              }}
            >
              {privacyLink && (
                <Link
                  href={privacyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Privacy Policy
                </Link>
              )}

              {termsLink && (
                <Link
                  href={termsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Terms of Service
                </Link>
              )}

              {supportEmail && (
                <Link
                  href={`mailto:${supportEmail}`}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Contact Support
                </Link>
              )}
            </Box>
          )}
        </Box>

        {/* Powered by section (optional) */}
        {getConfigValue<boolean>('SHOW_POWERED_BY', false) && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderTopColor: 'grey.200',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              Powered by{' '}
              <Link
                href="https://brightmls.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'secondary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                BrightMLS
              </Link>
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SaasFooter;
